/**
 * The drafting surface, in pieces.
 *
 * Everything here exists because of a rule, and the ones that look like polish
 * are the ones that matter most:
 *
 * **The diff card** is the enforcement mechanism of rule 2. Current struck in
 * red, proposed in green, Accept / Reject / Try again, and text is *never*
 * swapped in place. Only Accept writes. Reject leaves no trace on the document.
 *
 * **Blank chips** are amber, countable and clickable, counting down to "All
 * blanks filled". A value the model declined to invent reads differently from a
 * field the system could not resolve, because they are different jobs of work.
 *
 * **`[S]` refuses before any call.** Selecting inside statutory text does not
 * open an instruct bar that then fails — the bar is not offered, and the reason
 * is on screen. The refusal is on the server too; this is the half that means
 * the officer never gets as far as asking.
 *
 * **Blocked stays on screen, greyed, with the rule.** Download while a BLOCK
 * stands is disabled and visible, never hidden.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  checkKey,
  draftBadge,
  groupSources,
  plainWords,
  readBlankCount,
  readDay,
  readOtherChecks,
  readSlotName,
  readStops,
  readUndecided,
  segment,
  sortChecks,
} from '../lib/blocks'
import { readDraft } from '../lib/drafting'
import { readDate } from '../lib/period'
import { placeBeside } from '../lib/beside'
import { api } from '../api/client'
import { CitationChip, ErrorEnvelope } from './bits'
import type {
  BlockView,
  CheckView,
  DocumentVersionSummary,
  DocumentVersionView,
  DraftingNeedsView,
  DraftingNeedView,
  MarkView,
  MarksView,
  RewriteProposalView,
  UnresolvedSlotView,
} from '../api/client'

/* ------------------------------------------------------------ the document */

/**
 * The head of the page, as it prints.
 *
 * `typed` is a memorandum's: the organisation's lines stacked on the right,
 * between the number and the date. Otherwise it is the printed letterhead of a
 * letter: its two columns side by side and a rule under them. Either way the
 * address lines are shown small beneath, said to be for the foot — the Word
 * file prints them at the foot of the last page, never in the head.
 */
function PrintedHead({
  head,
  typed,
}: {
  head: NonNullable<BlockView['head']>
  typed: boolean
}) {
  const lines = head.lines ?? []
  const beside = head.second_column ?? []
  const foot = head.foot ?? []
  const columns = typed ? [lines] : [lines, beside].filter((column) => column.length > 0)
  return (
    <div
      className={typed ? 'head typed' : 'head printed'}
      data-testid={typed ? 'head-typed' : 'head-printed'}
    >
      <div className="columns">
        {columns.map((column, at) => (
          <div key={at}>
            {column.map((line, index) => (
              <div key={index} className={!typed && index === 0 ? 'first' : undefined}>
                {line}
              </div>
            ))}
          </div>
        ))}
      </div>
      {foot.length > 0 ? (
        <div className="at-foot" title="Printed at the foot of the last page, not in the head.">
          {foot.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/* ----------------------------------------------------- beside the passage --
 *
 * **The controls go to the passage, not the passage to the controls.**
 *
 * They used to render after the document, so an officer who selected a
 * paragraph on page one scrolled past every page to reach the buttons, acted,
 * and scrolled back. On a three-page ज्ञापन that is the document's length twice
 * for one rewrite.
 *
 * Anchored to the live `Range`, not to a rectangle captured at selection time:
 * the document scrolls inside `.main`, not the window, so a measurement taken
 * once drifts away from its own paragraph the moment the officer scrolls. The
 * range is re-measured on every scroll and resize, which costs one
 * `getBoundingClientRect` per animation frame and is the only version that is
 * still beside the right words a second later.
 */
export function AtSelection({ range, children }: { range: Range; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement | null>(null)
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: 'hidden',
    position: 'fixed',
  })

  useLayoutEffect(() => {
    let frame = 0
    const place = () => {
      frame = 0
      const el = box.current
      if (!el) return
      const at = range.getBoundingClientRect()
      // **Vertical from the passage, horizontal from the page.** Anchoring the
      // left edge to the selection as well looks right for a selection that
      // wraps — its box is the paragraph's — and wrong for every short one: a
      // dozen words near the right margin has `left` three-quarters across the
      // paper, and a 620px panel from there sits half off the document. The
      // column is the steady thing, so the bar shares its left edge and only
      // moves up and down.
      const column = range.startContainer.parentElement?.closest('.draft') ?? null
      const left = column ? column.getBoundingClientRect().left : at.left
      // The document bar is sticky, so the top of the window is not the top of
      // the usable area. Measured from the bar itself rather than written down
      // as a number: it wraps to two lines on a narrow window, and a hardcoded
      // inset would be wrong on exactly the screens where the room is tightest.
      const bar = el.ownerDocument.querySelector('.docbar')
      const inset = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0
      const where = placeBeside(
        { top: at.top, bottom: at.bottom, left },
        { width: el.offsetWidth || 620, height: el.offsetHeight || 200 },
        { width: window.innerWidth, height: window.innerHeight },
        undefined,
        inset,
      )
      // Only when it actually moved: `place` runs once per animation frame
      // while the officer scrolls, and setting state unconditionally would
      // re-render the diff card under their cursor on every one of them.
      setStyle((was) =>
        was.top === where.top && was.left === where.left && was.visibility === 'visible'
          ? was
          : { position: 'fixed', ...where, visibility: 'visible' },
      )
    }
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(place)
    }
    place()
    // Capture, because the scroller is `.main` and not the window.
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    // **And when the panel itself changes height.** The same anchor holds the
    // instruct bar, the diff card and the statutory refusal, and they are not
    // the same size: proposing a rewrite swaps a 130px bar for a card twice
    // that, and without this the taller card keeps the shorter one's position
    // and runs off the bottom of the screen — the Reject button first.
    const watch = new ResizeObserver(schedule)
    if (box.current) watch.observe(box.current)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      watch.disconnect()
    }
  }, [range])

  return (
    <div className="at-selection" style={style} ref={box} data-testid="selection-anchor">
      {children}
    </div>
  )
}

export function DraftView({
  version,
  caseId,
  onFill,
  activeSlot,
  onCommit,
  onCancelFill,
  busy,
  onSelect,
  onRefuse,
  readOnly,
  readOnlyReason,
  onMark,
  openMark,
}: {
  version: DocumentVersionView
  caseId: string
  onFill: (chip: UnresolvedSlotView) => void
  /** The slot being typed into, if any — at most one on the page. */
  activeSlot: string | null
  onCommit: (slot: string, value: string) => void
  onCancelFill: () => void
  busy: boolean
  onSelect: (block: BlockView, start: number, end: number, range: Range) => void
  onRefuse: (reason: string, range?: Range) => void
  readOnly: boolean
  readOnlyReason: string | null
  onMark?: (mark: MarkView) => void
  /** The mark whose reason is open, so the words it is about stay findable. */
  openMark?: string | null
}) {
  const chips = useMemo(
    () => new Map((version.unresolved ?? []).map((chip) => [chip.slot, chip])),
    [version.unresolved],
  )
  /* **No "page 1 ends" rules.** They were drawn every 973px down the sheet —
   * one printed page of content — and they went for two reasons, either enough.
   *
   * They were never in the document's flow. Each was an absolutely placed
   * element emitted *before the first paragraph*, moved down the sheet only by
   * a CSS offset. So everything that reads the page in order — the page's
   * text, a selection copied into Word — met `page 1 ends`, `page 2 ends`
   * stacked at the head of the document (walkthrough 32.34), and because the
   * markers hung below a sheet that had since got shorter they were counted in
   * its height and never went away.
   *
   * And 973px down this sheet is not where the paper ends. The sheet starts 42px
   * of padding in; it carries things that never print — the flags in the
   * margin, the *From:* line under a paragraph, the name under every blank,
   * a waiting paragraph's placeholder; and it is set in a different face from
   * the DOCX. Word decides the breaks when the file is opened, and nothing the
   * API sends says where they fell. A rule that says *the page ends here*
   * where it does not is worse than no rule (the old comment said so itself).
   * When the renderer can say where a page ends, this is where it is drawn. */
  return (
    <div className="draft deva" data-testid="draft">
      {(version.blocks ?? [])
        // A paragraph the app left out — an optional passage for another
        // purpose, a banner omitted on a first noting, a proposal a start
        // noting does not make — has no text and nothing to act on. Drawn, it
        // was a locked tag over an empty line. One still being drafted stays.
        .filter(
          (block) =>
            (block.text_hi ?? '').trim() !== '' ||
            block.draft_state === 'pending' ||
            block.draft_state === 'drafting',
        )
        .map((block) => (
          <BlockRow
            key={`${block.part}-${block.order}`}
            block={block}
            caseId={caseId}
            chips={chips}
            onFill={onFill}
            activeSlot={activeSlot}
            onCommit={onCommit}
            onCancelFill={onCancelFill}
            busy={busy}
            onSelect={onSelect}
            onRefuse={onRefuse}
            readOnly={readOnly}
            readOnlyReason={readOnlyReason}
            marks={(version.marks?.marks ?? []).filter((m) => m.block === block.order)}
            onMark={onMark}
            openMark={openMark ?? null}
          />
        ))}
    </div>
  )
}

/**
 * A text segment, cut at the marks that fall inside it.
 *
 * ADR-025: a groundedness mark is drawn **on the words themselves**, because a
 * list of six problems above a document is a list read once and scrolled past.
 * The offsets are in `text_hi`'s coordinates — the same coordinates `pick`
 * measures selections in and for the same reason: a blank is seventeen stored
 * characters drawn as five, so anything counting rendered characters lands in
 * the wrong place.
 *
 * Marks that do not touch this segment are skipped rather than clamped. A mark
 * clamped to a segment boundary is a mark drawn on words it is not about.
 */
function cutAtMarks(
  value: string,
  start: number,
  marks: MarkView[],
): { text: string; at: number; mark: MarkView | null }[] {
  const end = start + value.length
  const inside = marks
    .filter((m) => m.start < end && m.end > start)
    .sort((a, b) => a.start - b.start)
  if (!inside.length) return [{ text: value, at: start, mark: null }]

  const out: { text: string; at: number; mark: MarkView | null }[] = []
  let cursor = start
  for (const mark of inside) {
    const from = Math.max(mark.start, start)
    const to = Math.min(mark.end, end)
    if (from > cursor)
      out.push({ text: value.slice(cursor - start, from - start), at: cursor, mark: null })
    if (to > from) out.push({ text: value.slice(from - start, to - start), at: from, mark })
    cursor = Math.max(cursor, to)
  }
  if (cursor < end) out.push({ text: value.slice(cursor - start), at: cursor, mark: null })
  return out
}

function BlockRow({
  block,
  caseId,
  chips,
  onFill,
  activeSlot,
  onCommit,
  onCancelFill,
  busy,
  onSelect,
  onRefuse,
  readOnly,
  readOnlyReason,
  marks,
  onMark,
  openMark,
}: {
  block: BlockView
  caseId: string
  chips: Map<string, UnresolvedSlotView>
  onFill: (chip: UnresolvedSlotView) => void
  activeSlot: string | null
  onCommit: (slot: string, value: string) => void
  onCancelFill: () => void
  busy: boolean
  onSelect: (block: BlockView, start: number, end: number, range: Range) => void
  onRefuse: (reason: string, range?: Range) => void
  readOnly: boolean
  readOnlyReason: string | null
  marks: MarkView[]
  onMark?: (mark: MarkView) => void
  openMark: string | null
}) {
  // The officer's words for the flag, not the template editor's (stop list 8:
  // it stays flagged, on every paragraph that is ours).
  const badge = draftBadge(block.provenance)
  // The blank's reason is already on the version, recorded against this block
  // as an unresolved chip keyed `b<order>`. It just never reached the strip.
  const draft = readDraft(block, chips.get(`b${block.order}`)?.reason)
  const locked = block.provenance === 'S' || block.locked
  const papers = groupSources(block.sources ?? [])

  /**
   * The span in `text_hi`'s coordinates — **not the rendered page's.**
   *
   * The two differ, and by a lot. A blank is `⟦enclosure_pages⟧` in the stored
   * text, seventeen characters, and it is drawn as five `＿`. Counting the
   * rendered characters would put every selection after a blank twelve
   * characters early, and `accept_proposal` re-verifies the span against the
   * stored text before it writes: the lucky outcome is a refusal the officer
   * cannot explain, and the unlucky one is a rewrite spliced into a passage it
   * was never written against.
   *
   * So each text segment carries the offset `segment()` measured for it, and
   * the position is that offset plus the distance into that segment. A
   * selection that starts or ends inside a blank is not a passage — it is a
   * value the officer has not supplied yet — and is ignored.
   */
  const pick = (event: React.MouseEvent<HTMLDivElement>) => {
    // **A locked block reports the selection too.** Bailing here would make
    // selecting statutory text do nothing whatever — no bar, no message, no
    // call — and an officer who is refused in silence learns only that the tool
    // is unreliable. Rule 3: the refusal stays on screen with its rule. What
    // never happens is the request; that is decided a layer up, where the
    // selection resolves to `StatutoryRefusal` instead of the instruct bar.
    //
    // **And then every other path bailed silently anyway.** That comment
    // described one guard and four more sat under it returning nothing: an
    // issued document, the English view, a selection crossing two paragraphs,
    // and a boundary landing inside a blank. An officer dragged across three
    // reference lines, got nothing at all, and had no way to learn which of the
    // four it was. Each one now says which.
    //
    // **The range is taken before anything is refused**, because a refusal
    // that cannot say where it happened has to be printed at the bottom of the
    // page, which is the defect this whole anchor exists to remove. It also
    // puts the collapsed-selection test ahead of the read-only one: a plain
    // click on an issued document is not a rewrite attempt and should not
    // answer back.
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) return
    const container = event.currentTarget
    const range = selection.getRangeAt(0)
    const here = range.cloneRange()
    if (readOnly) {
      onRefuse(
        readOnlyReason ??
          'This document cannot be changed here. Nothing was sent and nothing was written.',
        here,
      )
      return
    }
    if (!container.contains(range.commonAncestorContainer)) {
      onRefuse(
        'A rewrite is proposed against one paragraph, and this selection covers more than ' +
          'one. Select within a single paragraph and ask again.',
        here,
      )
      return
    }

    const at = (node: Node, offset: number): number | null => {
      const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element)
      const host = element?.closest('[data-start]')
      if (!host || !container.contains(host)) return null
      const base = Number(host.getAttribute('data-start'))
      if (!Number.isFinite(base)) return null
      // How far into this segment the boundary sits, measured on the segment's
      // own text — which for a text segment is byte-for-byte what is stored.
      const within = document.createRange()
      within.selectNodeContents(host)
      within.setEnd(node, offset)
      return base + within.toString().length
    }

    const start = at(range.startContainer, range.startOffset)
    const end = at(range.endContainer, range.endOffset)
    if (start === null || end === null) {
      onRefuse(
        'The selection starts or ends inside a blank, which is a value the document does not ' +
          'have yet rather than a passage to rewrite. Select the words either side of it.',
        here,
      )
      return
    }
    if (end <= start) return
    onRefuse('')
    // A clone, because the browser's own selection is cleared the moment the
    // officer clicks a button in the bar.
    onSelect(block, start, end, here)
  }

  return (
    <section
      className="block"
      data-provenance={block.provenance ?? 'none'}
      data-draft-state={block.draft_state}
      data-locked={locked ? 'true' : 'false'}
      // What the paragraph is on the page, as the server reads it — the same
      // reading the Word file is set from. It only positions the text: the
      // characters under `.text` stay byte for byte what is stored, which is
      // what `pick` measures a selection against.
      data-layout={block.layout ?? 'none'}
      data-flows={block.flows ? 'true' : 'false'}
      data-testid={`block-${block.order}`}
    >
      <div className="gutter">
        {badge ? (
          <span className={`badge ${badge.tone}`} title={badge.title}>
            {badge.label}
          </span>
        ) : null}
        {(block.citations ?? []).map((citation) => (
          <CitationChip key={citation} citation={citation} />
        ))}
        {/* Never cleared to tidy a screen (stop list 10). */}
        {block.provenance === 'S' && !block.verified ? (
          <span
            className="unverified"
            title="Not yet checked against an official Hindi publication of the rules."
          >
            UNVERIFIED
          </span>
        ) : null}
        {/* [D]-grade: the machine's provisional English, never the office's
            words. Flagged on the face of the draft until the office publishes
            its own text over it (entry 10, stop list 8). */}
        {block.en_provisional ? (
          <span
            className="unverified"
            data-testid={`en-provisional-${block.order}`}
            title="Provisional English — machine-translated from the office's Hindi, not the office's own words. Check it before this document is served; it is replaced when the office publishes its English."
          >
            PROVISIONAL EN
          </span>
        ) : null}
      </div>

      {draft.label && !draft.awaiting ? (
        <div className={`strip ${draft.tone}`} data-testid={`strip-${block.order}`}>
          {draft.label}
        </div>
      ) : null}

      {/* **Rule 4's stack is off the page, not out of the record (ADR-027).**
          `Detected:` used to stand above `Adjusted by` with the superseded
          wording in full, so a paragraph an officer had rewritten showed the
          machine's draft louder than the officer's text and the officer read
          their own edit underneath the thing they had just replaced.

          Archit, 17 Sep 2026: the officer accepted the change, so they know
          what it said. What the rule protects is not that officer at that
          moment — it is whoever reads the file afterwards, and **that is
          intact**: `block.authorship` is still written, still travels on the
          version, and every superseded wording is still readable by opening
          the version it belongs to. Nothing is deleted; the disagreement moved
          from beside the paragraph to the version list above it. */}

      {/* **A paragraph that has not arrived is said to be on its way, and
          nothing else.** What the block holds until then is the template's own
          scaffold — `2.0 ⟦body_paras⟧`, `⟦proposal⟧` — which is storage, not
          text: drawn, it showed an officer raw markers under the word *Queued*
          and offered them a blank to fill that the draft was about to write
          over (walkthrough 32.34). So while it is awaited none of it is drawn:
          the placeholder stands where the paragraph will. */}
      {draft.awaiting ? (
        <div className={`being-written ${draft.tone}`} data-testid={`waiting-${block.order}`}>
          {draft.label}
        </div>
      ) : null}

      {/* **A letterhead is drawn, not typed.** The factory publishes its
          letterhead as a typed picture of the printed one — two columns either
          side of a bar, a row of rule characters, the address lines — and that
          is what the block stores. Shown as stored it was a column of bars over
          a line of dashes running off the paper (Archit, 5 Oct 2026). The
          server reads it into the lines it prints as, the same reading the
          Word file is set from, and those are drawn here. The stored text is
          untouched and stays in the page beneath, out of sight: nothing is
          selected from a letterhead to be rewritten. */}
      {block.head && !draft.awaiting ? (
        <PrintedHead head={block.head} typed={block.layout === 'typed-head'} />
      ) : null}

      <div className="text" onMouseUp={pick} hidden={draft.awaiting || Boolean(block.head)}>
        {(draft.awaiting ? [] : segment(block.text_hi)).map((part, index) => {
          // `data-start` is what `pick` measures from. Only text segments carry
          // it, so a selection inside a blank resolves to nothing and is
          // ignored rather than sent as a span the server cannot match.
          if (part.kind === 'text')
            return (
              <span key={index}>
                {cutAtMarks(part.value, part.start, marks).map((piece, at) =>
                  piece.mark ? (
                    <span
                      key={at}
                      data-start={piece.at}
                      // `data-mark` is how the list of marks finds these words
                      // to scroll to; `open` keeps them findable while their
                      // reason is being read.
                      data-mark={piece.mark.key}
                      className={[
                        'marked',
                        piece.mark.checked_at ? 'checked' : '',
                        openMark === piece.mark.key ? 'open' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      title={piece.mark.reason}
                      data-testid={`mark-${piece.mark.key}`}
                      onClick={() => onMark?.(piece.mark as MarkView)}
                    >
                      {piece.text}
                    </span>
                  ) : (
                    <span key={at} data-start={piece.at}>
                      {piece.text}
                    </span>
                  ),
                )}
              </span>
            )
          if (part.kind === 'undeclared') {
            return (
              <span
                key={index}
                className="blank undeclared"
                title={`{{${part.value}}} — a marker no slot table declares, so nothing asks you to fill it. Open item 26; it closes with the template editor.`}
              >
                ⟦{part.value}⟧
              </span>
            )
          }
          const chip = chips.get(part.value)
          const name = readSlotName(part.value, chip?.label_hi)
          // Two kinds of blank, and they must read differently (entry 24,
          // ruled 29 Aug 2026). A fillable one is the officer's, and it says
          // what it wants **under the rule** — the way a paper form prints
          // `____ (दिनांक)` — so the guidance is visible without hovering and
          // the line still reads like the paper it will become. The full
          // reason stays in the dialog and the tooltip; the under-label is
          // the name alone. A not-yours-to-fill gap (statute, unpublished
          // standing text) offers no input at all: the sentence saying why is
          // the whole surface, because inviting an officer to type a statute
          // is worse than the gap (rule 1, and the [S] lock).
          if (chip && chip.fillable === false) {
            return (
              <span
                key={index}
                className="blank unfillable"
                data-slot={part.value}
                title={`${name} — ${chip.reason ?? 'Not yours to fill; it is resolved at its source.'}`}
              >
                <span className="rule" aria-hidden="true">
                  ＿＿＿＿＿
                </span>
                <span className="blank-hint">{name} · not yours to fill</span>
              </span>
            )
          }
          return (
            /* **Filled where it sits, not in a drawer over the document.**
             *
             * The value belongs to the sentence around it — that sentence is
             * what tells the officer what to type — and the drawer covered it.
             * So a blank opens into an input in the line, Enter writes it and
             * moves to the next blank, and the officer never scans three pages
             * for the next amber chip.
             *
             * The drawer is still the right answer for the two cases that are
             * refusals rather than gaps: a list, and a gap that is not the
             * officer's to fill. Those need their reason on screen (rule 3),
             * not a text box. The route decides which. */
            activeSlot === part.value ? (
              <BlankInput
                key={index}
                chip={chip}
                name={name}
                busy={busy}
                onCommit={(value) => onCommit(part.value, value)}
                onCancel={onCancelFill}
              />
            ) : (
              <button
                key={index}
                type="button"
                className="blank"
                disabled={readOnly}
                data-slot={part.value}
                aria-label={name}
                title={blankTitle(name, chip)}
                onClick={() =>
                  onFill(
                    chip ??
                      ({
                        slot: part.value,
                        type: 'text',
                        is_list: false,
                      } as UnresolvedSlotView),
                  )
                }
              >
                <span className="rule" aria-hidden="true">
                  ＿＿＿＿＿
                </span>
                <span className={chip?.label_hi ? 'blank-hint deva' : 'blank-hint'}>
                  {name}
                </span>
                {/* **What it wants, and where to find it — on the document.**
                    The server sends both and they lived in a `title` attribute:
                    no use on a touch screen, none from the keyboard, and none
                    to an officer scanning seven chips working out which three
                    they can actually close this morning. */}
                {blankSource(chip) ? (
                  <span className="blank-source">{blankSource(chip)}</span>
                ) : null}
              </button>
            )
          )
        })}
      </div>

      {/* Which paper, which page — clickable to the scan. Not decoration: a
          live run found OCR fabricating a passage on a real scan's page 2, and
          nothing else flags that. This chip is the officer's only way to check
          a drafted paragraph against the paper it claims to rest on, so it
          opens the actual scan at the cited page in a new tab.

          **Each paper once, its pages together.** One report cited on ten
          pages was its file name ten times over, which reads as ten papers.
          Every page is still its own link to that page of the scan. */}
      {papers.length ? (
        <div className="sources small" data-testid={`sources-${block.order}`}>
          <span className="muted">From: </span>
          {papers.map((paper) => {
            const scan = api.sourceFilePath(caseId, paper.file_id)
            const first = paper.pages[0]
            return (
              <span className="source" key={paper.file_id}>
                <a
                  className="citation mono"
                  href={scan + (first ? `#page=${first}` : '')}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the scan — check the paragraph against the paper, not the paper against the paragraph."
                >
                  {paper.filename || 'paper'}
                </a>
                {paper.pages.length ? (
                  <span className="pages mono">
                    {' · '}
                    {paper.pages.map((page, at) => (
                      <span key={page}>
                        {at ? ', ' : ''}
                        <a
                          href={`${scan}#page=${page}`}
                          target="_blank"
                          rel="noreferrer"
                          title={`Open the scan at page ${page}.`}
                        >
                          p.{page}
                        </a>
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

/* ----------------------------------------------------------- blank filling */

const INPUT_TYPE: Record<string, string> = {
  date: 'date',
  number: 'number',
  amount: 'number',
}

export function FillDialog({
  chip,
  onCancel,
  onSubmit,
  busy,
  error,
}: {
  chip: UnresolvedSlotView
  onCancel: () => void
  onSubmit: (value: string) => void
  busy: boolean
  error: unknown
}) {
  const [value, setValue] = useState('')
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => input.current?.focus(), [])

  return (
    <>
      <div className="scrim" onClick={onCancel} />
      <aside
        className="drawer"
        role="dialog"
        aria-label={`Fill ${readSlotName(chip.slot, chip.label_hi)}`}
      >
        <header>
          <div className={chip.label_hi ? 'deva' : ''}>
            {readSlotName(chip.slot, chip.label_hi)}
          </div>
          <button type="button" className="quiet" onClick={onCancel}>
            Close
          </button>
        </header>
        <div className="body">
          {/* Why it is blank, in the server's words. A value the model declined
              to invent is a different thing from one the record never had. */}
          <p className="small muted">{chip.reason}</p>

          {/* **Said, not alarmed.** A blank that is not the officer's to fill is
              the app declining to invent a value (rule 1) — the product working.
              Both of these wore the red refusal box, which is what an error
              looks like on every other screen. */}
          {chip.fillable === false ? (
            <p data-testid="blank-not-yours">
              Not yours to fill. This gap closes at its source — statutory text comes from the
              rules as loaded, standing text is published by the factory&apos;s office — and a
              value typed here is not accepted, whoever types it.
            </p>
          ) : chip.is_list ? (
            <p data-testid="blank-is-a-list">
              This is a list — the articles of charge, the witnesses, the enclosures. It is not
              one blank, and there is no box to type it into here.
            </p>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault()
                if (value.trim()) onSubmit(value)
              }}
            >
              <label className="field">
                <span className="small muted">
                  {chip.type === 'date'
                    ? 'Entered once — it fills every place this slot appears, written 29 / 10 / 2020.'
                    : 'Entered once — it fills every place this slot appears.'}
                </span>
                <input
                  ref={input}
                  type={INPUT_TYPE[chip.type] ?? 'text'}
                  value={value}
                  className={chip.type === 'date' || chip.type === 'number' ? 'mono' : 'deva'}
                  onChange={(event) => setValue(event.target.value)}
                />
              </label>
              <ErrorEnvelope error={error} />
              <div className="row" style={{ marginTop: 12 }}>
                <button type="submit" className="btn" disabled={busy || !value.trim()}>
                  {busy ? 'Writing…' : 'Fill'}
                </button>
                <button type="button" className="quiet" onClick={onCancel}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </aside>
    </>
  )
}

/* -------------------------------------------------------------- the diff card */

export const INSTRUCTIONS = [
  'Shorten',
  'Elaborate',
  'More formal',
  'Simplify',
  'Re-translate',
] as const

export function InstructBar({
  selection,
  onAsk,
  onEdit,
  onDismiss,
  busy,
  error,
}: {
  selection: { block: BlockView; start: number; end: number; text: string }
  onAsk: (instruction: string) => void
  onEdit: () => void
  onDismiss: () => void
  busy: boolean
  error: unknown
}) {
  const [free, setFree] = useState('')
  return (
    <div className="instruct" data-testid="instruct-bar">
      {/* **No echo of the selection.** It used to quote the selected passage back
          — which the officer had just made and can see highlighted three
          centimetres away. Four lines of Devanagari repeated is what made this
          bar too tall to sit beside the text, which is why it ended up beneath
          the whole document. */}
      <div className="small muted">
        {selection.end - selection.start} characters in paragraph {selection.block.order}
      </div>
      <div className="row">
        {INSTRUCTIONS.map((instruction) => (
          <button
            key={instruction}
            type="button"
            className="quiet"
            disabled={busy}
            onClick={() => onAsk(instruction)}
          >
            {instruction}
          </button>
        ))}
      </div>
      <form
        className="row"
        style={{ marginTop: 8 }}
        onSubmit={(event) => {
          event.preventDefault()
          if (free.trim()) onAsk(free)
        }}
      >
        <input
          value={free}
          placeholder="or say what to change"
          onChange={(event) => setFree(event.target.value)}
        />
        <button type="submit" className="btn" disabled={busy || !free.trim()}>
          {busy ? 'Asking…' : 'Ask'}
        </button>
        <button type="button" className="quiet" onClick={onDismiss}>
          Cancel
        </button>
      </form>
      {/* The officer's own words, through the same versioning. Offered beside
          the model's instructions rather than hidden behind them: an officer who
          knows what they want to write should not have to ask a model first. */}
      <button
        type="button"
        className="quiet"
        onClick={onEdit}
        style={{ marginTop: 8 }}
        data-testid="edit-block"
      >
        Edit this block by hand
      </button>
      <ErrorEnvelope error={error} />
    </div>
  )
}

/**
 * A refusal that costs nothing, because it happens before the request.
 *
 * The server refuses this too. Both halves exist because a statutory passage
 * reaching a model is the failure — not the model's answer being rejected
 * afterwards.
 */
export function StatutoryRefusal({
  block,
  onDismiss,
}: {
  block: BlockView
  onDismiss: () => void
}) {
  return (
    <div className="instruct locked" data-testid="statutory-refusal">
      <div className="envelope" role="alert">
        <div>
          Statutory text — locked. It is reproduced verbatim and carries its citation; no
          rewrite is possible on it, by anyone. A paraphrased statute is a different statute.
        </div>
        <div className="rule">
          stop-list-10
          {block.citations?.[0] ? ` · ${block.citations[0]}` : ''}
        </div>
      </div>
      <button type="button" className="quiet" onClick={onDismiss}>
        Close
      </button>
    </div>
  )
}

export function DiffCard({
  proposal,
  onAccept,
  onReject,
  onRetry,
  busy,
  error,
}: {
  proposal: RewriteProposalView
  onAccept: () => void
  onReject: () => void
  onRetry: () => void
  busy: boolean
  error: unknown
}) {
  return (
    <div className="diff" data-testid="diff-card">
      <header>
        <strong>Proposed change</strong>
        <span className="small muted">
          Nothing is written until you accept. Reject leaves no trace on the document.
        </span>
      </header>
      <div className="side current" data-testid="diff-current">
        <div className="small muted">Current</div>
        <del className="deva">{proposal.old}</del>
      </div>
      <div className="side proposed" data-testid="diff-proposed">
        <div className="small muted">Proposed</div>
        <ins className="deva">{proposal.new}</ins>
      </div>
      {proposal.reason ? (
        <p className="small muted">
          <strong>Why:</strong> {proposal.reason}
        </p>
      ) : null}
      <ErrorEnvelope error={error} />
      <div className="row">
        <button type="button" className="btn" onClick={onAccept} disabled={busy}>
          Accept
        </button>
        <button type="button" className="quiet" onClick={onReject} disabled={busy}>
          Reject
        </button>
        <button type="button" className="quiet" onClick={onRetry} disabled={busy}>
          Try again
        </button>
      </div>
    </div>
  )
}

/* --------------------------------------------------------- the pre-issue panel */

/**
 * What stands between this document and going out — said first, in words.
 *
 * The panel printed the rule catalogue as stored: a rule id on every row, a
 * `needs:` list of record paths, the skeleton's markdown, a row labelled `BLOCK`
 * under a header reading *0 blocking*. All of it true and none of it readable by
 * the officer it was for (walkthrough 32.34).
 *
 * **What rule 3 requires is kept, and it is the first thing on the panel:**
 * each check that stops the document is listed, never folded, with the rule in
 * its own words and its citation. What stops the document is the server's
 * `blocking` set and nothing this screen works out (`web/CLAUDE.md`).
 *
 * **Everything else is one closed disclosure**, whose own line says how many
 * did not pass, how many nothing could decide — *could not be decided from the
 * record*, never *passed* — and how many passed. Closed is not hidden: the
 * counts are on the line, and they are counts of the rows under it.
 *
 * A rule's id and the record paths it reads are the catalogue's, and stay off
 * the page; the id is still on each row for whoever maintains the catalogue
 * (`data-rule`). Where a check is waiting on a blank **that is on this
 * document**, the way to that blank is offered by the blank's own name.
 */
export function PreIssuePanel({
  checks,
  blocking,
  drafting,
  unresolved,
  onGoToBlank,
}: {
  checks: CheckView[]
  blocking: CheckView[]
  drafting: boolean
  /** The blanks on this document, so a check can name the one it is waiting on. */
  unresolved?: UnresolvedSlotView[]
  onGoToBlank: (slot: string) => void
}) {
  const sorted = sortChecks(checks, blocking)
  const others = readOtherChecks(sorted)
  const blanks = new Map((unresolved ?? []).map((chip) => [chip.slot, chip]))
  const row = (check: CheckView, tone: string, effect?: string) => (
    <CheckRow
      key={checkKey(check)}
      check={check}
      tone={tone}
      effect={effect}
      blanks={blanks}
      onGoToBlank={onGoToBlank}
    />
  )

  return (
    <div className="card checks" data-testid="pre-issue">
      <header>
        <h2>Pre-issue checks</h2>
      </header>
      <div className="body">
        <p className={sorted.stops.length ? 'says stops' : 'says'} data-testid="pre-issue-says">
          {drafting
            ? 'Checked once the draft is written.'
            : checks.length === 0 && sorted.stops.length === 0
              ? 'No check is set for this document.'
              : readStops(sorted.stops.length)}
        </p>
        {sorted.stops.map((check) => row(check, 'bad'))}
        {others ? (
          <details data-testid="pre-issue-others">
            <summary className="small muted">Other checks — {others}</summary>
            {sorted.notPassed.length ? (
              <p className="small muted group">
                Did not pass. {sorted.notPassed.length === 1 ? 'It does' : 'They do'} not stop
                this document.
              </p>
            ) : null}
            {sorted.notPassed.map((check) =>
              row(
                check,
                'warn',
                // The server says where a rule bites. One enforced when the
                // case moves is reported here and is not this document's.
                check.enforced_at && check.enforced_at !== 'document'
                  ? 'Looked at again when the case moves on.'
                  : undefined,
              ),
            )}
            {sorted.undecided.length ? (
              <p className="small muted group">
                {readUndecided(sorted.undecided.length)} — for you to confirm by hand.
              </p>
            ) : null}
            {sorted.undecided.map((check) => row(check, 'neutral'))}
            {sorted.passed.length ? <p className="small muted group">Passed.</p> : null}
            {sorted.passed.map((check) => row(check, 'good'))}
          </details>
        ) : null}
      </div>
    </div>
  )
}

function CheckRow({
  check,
  tone,
  effect,
  blanks,
  onGoToBlank,
}: {
  check: CheckView
  tone: string
  effect?: string
  blanks: Map<string, UnresolvedSlotView>
  onGoToBlank: (slot: string) => void
}) {
  // Only the blanks that are on this document. A path the rule reads off the
  // case record is not something this page can take the officer to.
  const waitingOn = (check.missing ?? []).filter((slot) => blanks.has(slot))
  return (
    <div
      className={`check ${tone}`}
      data-testid={`check-${check.rule}`}
      data-rule={check.rule ?? undefined}
    >
      <div>
        {plainWords(check.text)} <CitationChip citation={check.citation} />
      </div>
      {effect ? <div className="missing">{effect}</div> : null}
      {waitingOn.length ? (
        <div className="missing">
          Fill:{' '}
          {waitingOn.map((slot, index) => {
            const chip = blanks.get(slot)
            return (
              <span key={slot}>
                {index ? ', ' : ''}
                <button
                  type="button"
                  className={chip?.label_hi ? 'link deva' : 'link'}
                  onClick={() => onGoToBlank(slot)}
                >
                  {readSlotName(slot, chip?.label_hi)}
                </button>
              </span>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ versions */

export function VersionList({
  versions,
  currentId,
  onOpen,
}: {
  versions: DocumentVersionSummary[]
  currentId: string
  onOpen: (versionId: string) => void
}) {
  return (
    <div className="card" data-testid="versions">
      <header>
        <h2>Versions</h2>
      </header>
      <div className="body">
        {versions.length === 0 ? <p className="muted">No version yet.</p> : null}
        {versions.map((row) => (
          <div
            key={row.document_id}
            className={`version${row.document_id === currentId ? ' current' : ''}`}
            data-testid={`version-${row.version}`}
          >
            {/* The name the page's own title gives it — *Draft 2*, not `v2`. */}
            <button type="button" className="quiet" onClick={() => onOpen(row.document_id)}>
              Draft {row.version}
            </button>
            <div>
              <div className="small">{row.produced_by}</div>
              <div className="small faint">
                {row.created_by ?? 'unknown'} · {readDate(readDay(row.created_at))}
                {row.template_version !== null && row.template_version !== undefined
                  ? ` · template v${row.template_version}`
                  : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------- English */

/**
 * Read-only, and the absence of affordances is the specification.
 *
 * No slot chips, no selection instruct, no edit, no download as the issued
 * instrument. Hindi is the document of record (rule 5); this is a reading of it,
 * and a screen that let someone work here would be inviting them to perfect the
 * wrong document.
 */
export function EnglishPane({
  blocks,
  unavailable,
}: {
  blocks: { text_en: string | null; kind: string }[]
  unavailable: string | null
}) {
  return (
    <div className="english" data-testid="english">
      <div className="watermark">Reference translation — not the issued document</div>
      {unavailable ? (
        <p className="envelope">{unavailable}</p>
      ) : (
        blocks.map((block, index) => (
          <p key={index} className={block.text_en ? '' : 'muted'}>
            {block.text_en
              ? // A blank is a ruled line here too — **not** the raw `⟦slot⟧`.
                // A token on a page reads as a bug the reader is meant to
                // ignore, which is item 34's finding, and it is no less true of
                // a reading than of the instrument. Still no chip and nothing to
                // click: the reader can see that a value is missing and has to
                // go to the Hindi to supply it, which is where it belongs.
                segment(block.text_en).map((part, at) =>
                  part.kind === 'text' ? (
                    <span key={at}>{part.value}</span>
                  ) : (
                    <span key={at} className="rule" aria-label={part.value}>
                      ＿＿＿＿＿
                    </span>
                  ),
                )
              : 'Not translated.'}
          </p>
        ))
      )}
    </div>
  )
}

/* -------------------------------------------------------------- the counter */

/**
 * Editing a block by hand, through the same versioning as everything else.
 *
 * Deliberately not a contenteditable that saves on blur. The officer is writing
 * a version of a document that gets served, and the act of pressing Save is the
 * act of saying so.
 */
export function EditBlockDialog({
  block,
  reviewBeforeSaving = false,
  onCancel,
  onSubmit,
  busy,
  error,
}: {
  block: BlockView
  reviewBeforeSaving?: boolean
  onCancel: () => void
  onSubmit: (text: string) => void
  busy: boolean
  error: unknown
}) {
  const [text, setText] = useState(block.text_hi)
  return (
    <>
      <div className="scrim" onClick={onCancel} />
      <aside className="drawer wide" role="dialog" aria-label={`Edit block ${block.order}`}>
        <header>
          <div>
            <div>Edit block {block.order}</div>
            <div className="small muted">{block.provenance ?? 'the skeleton tags nothing'}</div>
          </div>
          <button type="button" className="quiet" onClick={onCancel}>
            Close
          </button>
        </header>
        <div className="body">
          <p className="small muted">
            {reviewBeforeSaving
              ? 'Your wording will appear as a proposed change. Review it and choose Accept before it creates a new version.'
              : 'Your wording, written as a new version. The version before it stays readable, and what this block said before you changed it stays on the block.'}
          </p>
          <textarea
            className="deva"
            rows={12}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <ErrorEnvelope error={error} />
          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn"
              disabled={busy || !text.trim() || text === block.text_hi}
              onClick={() => onSubmit(text)}
            >
              {busy
                ? reviewBeforeSaving
                  ? 'Preparing…'
                  : 'Writing…'
                : reviewBeforeSaving
                  ? 'Review proposed change'
                  : 'Save as a new version'}
            </button>
            <button type="button" className="quiet" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

/**
 * The pinned template, read-only.
 *
 * Every generated document is stamped permanently with the template version
 * that produced it, and this is the click-through to that version — not the
 * current one. A drawer that showed today's template beside a document built
 * from last month's would answer the question wrongly and look right doing it.
 */
export function TemplateDrawer({
  name,
  version,
  onClose,
}: {
  /** The document's name off the pinned process version — never its code. */
  name: string
  version: number
  onClose: () => void
}) {
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="Pinned template">
        <header>
          <div>
            <div>
              {name} · template version {version}
            </div>
            <div className="small muted">pinned to this document, permanently</div>
          </div>
          <button type="button" className="quiet" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="body">
          <p className="small muted">
            This document was assembled from version {version} of its template. Publishing a new
            template affects new documents only — this one keeps the version that produced it,
            so what it says can always be traced to what it was built from.
          </p>
          <p className="small faint">The template itself is read under Templates.</p>
        </div>
      </aside>
    </>
  )
}

/**
 * Marking a document issued. **The serial is consumed here, not at generation.**
 *
 * A draft that is never signed out must not eat a number, and once taken a
 * number is never reused — a withdrawn document keeps its own, because case
 * files years old still refer to it.
 */
export function IssuePanel({
  name,
  which,
  act,
  blocking,
  onIssue,
  busy,
  error,
}: {
  /** The document's name off the pinned process version — never its code. */
  name: string
  /** Which one of several this is, or null where there is only the one. */
  which: string | null
  /**
   * The act in the case card's own words — *It has been sent up* for a noting,
   * *It has been issued* for an instrument — where the card is offering it for
   * this document. The server knows which way a document goes; this screen does
   * not, and falls back to the plain word.
   */
  act?: string | null
  blocking: number
  onIssue: (issuedOn: string) => void
  busy: boolean
  error: unknown
}) {
  const [issuedOn, setIssuedOn] = useState('')
  const say = act || 'Mark issued'
  return (
    <div className="card" data-testid="issue">
      <header>
        <h2>Issue</h2>
        <span className="small muted">
          {name}
          {which ? ` · ${which}` : ''}
        </span>
      </header>
      <div className="body">
        {blocking > 0 ? (
          <div className="blocked" data-testid="issue-blocked">
            <button type="button" disabled>
              {say}
            </button>
            <div>
              <div className="why">
                {readStops(blocking)} {blocking === 1 ? 'It is' : 'They are'} listed under the
                checks above. The document cannot be downloaded, so it has not been signed.
              </div>
            </div>
          </div>
        ) : (
          <div className="row form">
            <label className="field">
              <span className="small muted">Date it went out</span>
              <input
                type="date"
                className="mono"
                value={issuedOn}
                onChange={(event) => setIssuedOn(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn"
              disabled={busy || !issuedOn}
              onClick={() => onIssue(issuedOn)}
              data-testid="mark-issued"
            >
              {busy ? 'Recording…' : say}
            </button>
            {/* Said before the act, not found out after it: the document goes
                out bearing this date, so its own date line is made to read it. */}
            <span className="small muted" data-testid="issue-date-says">
              The document's date line is set to this date. The draft as it stands is kept.
            </span>
          </div>
        )}
        <ErrorEnvelope error={error} />
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- the counter */

/* ------------------------------------------------------- what a blank wants --
 *
 * Two readings of the same three facts the server already sends — `source`,
 * `type` and `reason`. The short one sits under the name on the document, so an
 * officer scanning seven chips can see which they can close without opening
 * any of them. The long one is the tooltip, and it is the one Archit asked to
 * carry "what it is expecting and where the officer can find the value".
 *
 * `source` is the load-bearing word. `case` means the record could have
 * supplied this and did not; `officer` means it was never going to — it is on
 * the physical file and only a person has it. Those are different problems and
 * an officer should not have to open a drawer to tell them apart.
 *
 * **A blank on the page is the officer's to fill, and says so.** The first
 * reading called a `case` slot *from the record* — on a blank, which is on the
 * page precisely because the record did **not** supply it. So the folio number
 * and the count of pages read as values the record held, and an officer waited
 * for a number that was never coming (walkthrough 32.34). Whose it is comes
 * first; where to find it follows. A blank is the app declining to invent a
 * value (rule 1), and it reads as work to do, never as something gone wrong.
 */
const SLOT_WANTS: Record<string, string> = {
  date: 'a date',
  amount: 'an amount',
  person: 'a name',
  number: 'a number',
  'ref-no': 'a reference number',
  text: 'text',
}

export function blankSource(chip: UnresolvedSlotView | undefined): string {
  if (!chip) return ''
  if (chip.fillable === false) return 'not yours to fill'
  return chip.source === 'officer'
    ? 'yours to fill — from the file'
    : 'yours to fill — not on the record'
}

export function blankTitle(name: string, chip: UnresolvedSlotView | undefined): string {
  if (!chip) return `${name} — the case record does not supply this yet.`
  const wants = SLOT_WANTS[chip.type ?? 'text'] ?? 'a value'
  const where =
    chip.fillable === false
      ? 'This one is not yours to fill — it closes at its source.'
      : chip.source === 'officer'
        ? 'The register does not hold this. It is on the physical file, and it is yours to supply.'
        : 'The case record does not hold this, so it is yours to fill here.'
  return `${name}\nWants ${wants}. ${where}\n${chip.reason ?? ''}`.trim()
}

/* The blank, open. Sized to the text so the line does not jump as it is typed
   — the paper renders 1:1 with what prints, and a field that reflows the
   paragraph while an officer types into it is reading the document wrong. */
export function BlankInput({
  chip,
  name,
  busy,
  onCommit,
  onCancel,
}: {
  chip: UnresolvedSlotView | undefined
  name: string
  busy: boolean
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState('')
  const box = useRef<HTMLInputElement>(null)
  useEffect(() => {
    box.current?.focus()
    box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [])
  const deva = chip?.type !== 'date' && chip?.type !== 'number' && chip?.type !== 'amount'
  return (
    <span className="blank open" data-slot={chip?.slot}>
      <input
        ref={box}
        className={deva ? 'deva' : 'mono'}
        type={INPUT_TYPE[chip?.type ?? 'text'] ?? 'text'}
        value={value}
        size={Math.max(name.length, value.length + 2, 8)}
        aria-label={name}
        disabled={busy}
        placeholder={name}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Enter writes it and goes to the next. Escape leaves it as it was —
          // and neither of them is a mouse, because filling a document is a
          // typing job and the hands are already on the keys.
          if (e.key === 'Enter') {
            e.preventDefault()
            if (value.trim()) onCommit(value.trim())
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        onBlur={() => {
          // A click elsewhere abandons an empty blank rather than trapping the
          // officer in it; a typed one is kept open so the keystrokes are not
          // thrown away by a stray click.
          if (!value.trim()) onCancel()
        }}
      />
    </span>
  )
}

export function BlankCounter({ remaining }: { remaining: number }) {
  return (
    <span
      className={`chip ${remaining > 0 ? 'warn' : 'good'}`}
      data-testid="blank-counter"
      data-remaining={remaining}
    >
      {readBlankCount(remaining)}
    </span>
  )
}

/**
 * How many paragraphs are our own unsourced draft.
 *
 * `[D]` means we wrote the words with no AWEIL sample and no prescribed form
 * behind them, and stop list 8 requires it to be visibly flagged and cleared
 * before real use. The badge in a paragraph's gutter says so where the
 * paragraph is; nothing said so where an officer looks *first*, and a document
 * can be most of the way unsourced without that ever being one glance away.
 *
 * Loudest thing in the toolbar on purpose — it outranks the blank counter,
 * because an unfilled blank is visible on the page and an unsourced paragraph
 * reads exactly like settled office text.
 */
export function UnsourcedCount({ blocks }: { blocks: BlockView[] }) {
  // Only what prints. A paragraph the app left out of this document arrives
  // empty, and counting it said *2 paragraphs are our own draft* over a page
  // showing one (run of 5 Oct 2026).
  const n = blocks.filter(
    (b) => (b.text_hi ?? '').trim() !== '' && (b.provenance === 'D' || b.unsourced),
  ).length
  if (n === 0) return null
  return (
    <span
      className="chip bad"
      data-testid="unsourced-count"
      data-count={n}
      title="Drafted by us. No AWEIL sample and no prescribed form behind it — it must be cleared before real use."
    >
      {n === 1 ? '1 paragraph is our own draft' : `${n} paragraphs are our own draft`}
    </span>
  )
}

/**
 * How many things on this document the officer has still to look at.
 *
 * ADR-025. Deliberately the same shape and the same shelf as the blank counter,
 * because it is the same kind of fact and the countdown is a pattern the
 * officer already knows: *6 things to check* down to *All checked*.
 *
 * **It never blocks.** A document may be issued with every mark outstanding —
 * the count is a prompt, not a gate. What makes that defensible is the other
 * half: checking one off is a recorded act with a person and a time, so the
 * file can afterwards say the officer was shown six things and cleared six,
 * rather than hoping he read forty fluent sentences.
 *
 * Says nothing at all when the check has not run — a document still drafting
 * has no marks yet, and a chip reading "All checked" over an unchecked document
 * would be the one lie this whole mechanism exists to prevent.
 *
 * **It opens the list.** It was a number that opened nothing: each reason lived
 * only in the hover text of an underline somewhere in a two-page document, so
 * *6 things to check* sent the officer hunting for six dotted underlines
 * (walkthrough 32.34). Wherever there is something to read — marks, or the
 * sentence saying the check could not run — the counter is the way to it.
 */
export function MarkCounter({
  marks,
  open,
  onToggle,
}: {
  marks: MarksView | null
  /** Whether the list is showing. */
  open?: boolean
  /** Opens or closes the list. Absent, the counter is a plain count. */
  onToggle?: () => void
}) {
  if (!marks) return null
  const left = marks.outstanding ?? 0
  const some = Boolean(marks.marks?.length)
  if (!marks.unavailable && !some) {
    return (
      <span className="chip good" data-testid="mark-counter" data-outstanding="0">
        Nothing to check
      </span>
    )
  }
  const tone = marks.unavailable || left > 0 ? 'warn' : 'good'
  const words = marks.unavailable
    ? 'not fully checked'
    : left > 0
      ? `${left} ${left === 1 ? 'thing' : 'things'} to check`
      : 'All checked'
  if (!onToggle) {
    return (
      <span
        className={`chip ${tone}`}
        data-testid="mark-counter"
        data-outstanding={marks.unavailable ? undefined : left}
        title={marks.unavailable ?? undefined}
      >
        {words}
      </span>
    )
  }
  return (
    <button
      type="button"
      className={`chip opens ${tone}`}
      data-testid="mark-counter"
      data-outstanding={marks.unavailable ? undefined : left}
      aria-expanded={Boolean(open)}
      onClick={onToggle}
    >
      {words}
    </button>
  )
}

/**
 * Every mark on the document, each with its reason — and the way to its words.
 *
 * ADR-025 puts a mark **on the words it is about**, because a list of problems
 * above a document is read once and scrolled past. That still holds: this list
 * is not where a mark is dealt with. Choosing one takes the officer to the
 * words and opens the same panel a click on the underline opens, where *I have
 * checked this* is recorded. The list is the map, in the document's own order.
 *
 * A checked mark stays in it, quiet, with who looked (rule 3).
 */
export function MarkList({
  marks,
  onChoose,
  onClose,
}: {
  marks: MarksView
  onChoose: (mark: MarkView) => void
  onClose: () => void
}) {
  const rows = marks.marks ?? []
  return (
    <div className="marklist" data-testid="mark-list">
      <div className="marklist-head">
        <strong>
          {rows.length
            ? `${rows.length} ${rows.length === 1 ? 'place' : 'places'} marked on this draft`
            : 'Nothing is marked on this draft'}
        </strong>
        <button type="button" className="quiet" onClick={onClose}>
          Close
        </button>
      </div>
      {/* A check that did not run is said in words, never shown as clean. */}
      {marks.unavailable ? (
        <p className="small" data-testid="marks-unavailable">
          {marks.unavailable}
        </p>
      ) : null}
      {rows.length ? (
        <ol>
          {rows.map((mark) => (
            <li key={mark.key} className={mark.checked_at ? 'checked' : undefined}>
              <button
                type="button"
                className="link deva"
                onClick={() => onChoose(mark)}
                data-testid={`go-to-mark-${mark.key}`}
              >
                {mark.text}
              </button>
              <span className="small">{mark.reason}</span>
              {mark.checked_at ? (
                <span className="small faint">Checked by {mark.checked_by}.</span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  )
}

/**
 * One mark, opened.
 *
 * The reason in the officer's words — never a rule code — and one act. The act
 * is deliberately *"I have checked this"* and not *"this is correct"*: the
 * record needs to know a person read it and decided, and asking him to certify
 * a value he may have decided to leave alone would be asking for something else
 * entirely.
 */
export function MarkPanel({
  mark,
  onCheck,
  onClose,
  busy,
}: {
  mark: MarkView
  onCheck: () => void
  onClose: () => void
  busy: boolean
}) {
  return (
    <div className="markpanel" data-testid="mark-panel">
      <p className="deva quoted">{mark.text}</p>
      <p className="small">{mark.reason}</p>
      {mark.checked_at ? (
        <p className="small faint" data-testid="mark-checked-by">
          Checked by {mark.checked_by}.
        </p>
      ) : null}
      <div className="row">
        {mark.checked_at ? null : (
          <button type="button" className="btn" disabled={busy} onClick={onCheck}>
            {busy ? 'Recording…' : 'I have checked this'}
          </button>
        )}
        <button type="button" className="quiet" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------- what the draft is written from --
 *
 * **Asked there and then, before drafting** (A11, walkthrough 32.9). When the
 * document is drafted from a paper, a decision or an act the file does not
 * have, the draft action says so before it drafts: upload the paper here,
 * record the decision or the act on the case page, where the card offers it —
 * or draft anyway, and each gap is marked in the draft and said on its record.
 *
 * Nothing is refused: the app never refuses to draft. And nothing here works
 * out what is missing — the list is the server's (`GET …/drafting-needs`), read
 * off the same cut the drafting call makes, so the question asked here and the
 * draft's NOT ON THE FILE cannot disagree.
 */
export function DraftingNeedsPanel({
  caseId,
  needs,
  onDraft,
  onCancel,
  onChanged,
  drafting,
  draftLabel,
}: {
  caseId: string
  needs: DraftingNeedsView
  onDraft: () => void
  onCancel: () => void
  /** A paper went onto the file here: ask the server again what is still missing. */
  onChanged: () => void
  drafting: boolean
  draftLabel: string
}) {
  const open = needs.needs ?? []
  const count = open.length
  return (
    <div className="card" data-testid="drafting-needs">
      <header>
        <h2>
          {count === 0
            ? 'Everything this draft is written from is on the file'
            : `Before drafting: ${count} ${count === 1 ? 'thing this draft is' : 'things this draft is'} written from ${count === 1 ? 'is' : 'are'} not on the file`}
        </h2>
      </header>
      <div className="body">
        {count ? (
          <p className="small muted">
            Upload a paper here, or record a decision or a date on the case page, where the card
            offers it. Or draft anyway: each gap is marked in the draft for you to fill, and the
            draft's record says what it was drafted without.
          </p>
        ) : null}
        {count ? (
          <ul className="drafting-needs" aria-label="Not on the file">
            {open.map((need) => (
              <NeedRow
                key={`${need.what}-${need.key}`}
                caseId={caseId}
                need={need}
                onChanged={onChanged}
              />
            ))}
          </ul>
        ) : null}
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={onDraft}
            disabled={drafting}
            data-testid={count ? 'draft-anyway' : 'draft-now'}
          >
            {drafting
              ? 'Assembling…'
              : count
                ? 'Draft anyway — the gaps are marked'
                : draftLabel}
          </button>
          <button type="button" className="quiet" onClick={onCancel} disabled={drafting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

/** One missing thing, with its way round: a paper is uploaded here; a decision
 *  or an act is recorded on the case page, where the card offers it. */
function NeedRow({
  caseId,
  need,
  onChanged,
}: {
  caseId: string
  need: DraftingNeedView
  onChanged: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [kindNotRecorded, setKindNotRecorded] = useState<string | null>(null)
  const upload = useMutation({
    mutationFn: async () => {
      if (!file || !need.paper_kind) throw new Error('Choose the paper first.')
      // Filed as the external document it is, where it is one (the enquiry
      // report, the inquiry report, the penalty order) — the upload's own
      // recording, the same path the case page's card takes.
      const receipt = await api.uploadSourceFile(caseId, file, {
        document_type: need.records_document ?? undefined,
      })
      try {
        await api.setPaperKind(caseId, receipt.file.id, {
          preflight: false,
          kind: need.paper_kind,
        })
        setKindNotRecorded(null)
      } catch (error) {
        // On the file either way; the kind not recorded is said, not hidden.
        setKindNotRecorded(error instanceof Error ? error.message : 'the kind was not recorded')
      }
    },
    onSuccess: () => {
      setFile(null)
      onChanged()
    },
  })
  return (
    <li data-testid={`need-${need.key}`}>
      <div>{need.words}</div>
      {need.what === 'paper' && need.paper_kind ? (
        <div className="row">
          <label className="small">
            {need.paper_label ?? 'The paper'}
            <input
              type="file"
              aria-label={need.paper_label ?? 'The paper'}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              data-testid={`need-file-${need.key}`}
            />
          </label>
          <button
            type="button"
            className="quiet"
            disabled={!file || upload.isPending}
            onClick={() => upload.mutate()}
            data-testid={`need-upload-${need.key}`}
          >
            {upload.isPending ? 'Uploading…' : 'Upload it'}
          </button>
          {kindNotRecorded ? (
            <span className="small warn-text">
              The paper is on the file; what kind it is was not recorded — {kindNotRecorded}
            </span>
          ) : null}
          <ErrorEnvelope error={upload.error} />
        </div>
      ) : (
        <div className="small">
          <Link to={`/cases/${caseId}`} data-testid={`need-record-${need.key}`}>
            Record it on the case page
          </Link>
        </div>
      )}
    </li>
  )
}
